import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRightLeft, ArrowUpRight, Copy, Download, Mail, MoreHorizontal, Pencil, Send, Trash2 } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { downloadDocPdf, getSendPreview, type EmailMeta, type SalesSendPreview } from "../../../lib/sales";
import { Pop, useClosePops } from "../../shell/Pop";
import { Btn, Modal, useToast } from "../../ui";
import { SendConfirmModal } from "../../mail/SendConfirmModal";
import { SHARE_TYPE, type AnyDoc, type DocConfig } from "./config";
import { sendPreviewFor } from "./sendPreview";

/**
 * The 44px column at the end of a sales document row.
 *
 * Everything the list can do to a document without opening it. The entries are
 * built from the kind's own config rather than from a fixed list: a credit note
 * cannot be sent and an invoice cannot be converted into anything, and a menu
 * that offers either would be offering an endpoint that does not exist. What
 * writes is behind `canManage`, so a read-only operator gets the two entries
 * that only read.
 */
export function DocRowMenu({
  doc,
  cfg,
  canManage,
  canMail,
  onChanged,
  onDeleted,
}: {
  doc: AnyDoc;
  cfg: DocConfig;
  canManage: boolean;
  /** `manage:crm.email`: the cover-letter send opens the mailbox, which needs it. */
  canMail: boolean;
  onChanged: (next: AnyDoc) => void;
  onDeleted: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const close = useClosePops();
  const navigate = useNavigate();
  const [confirmDel, setConfirmDel] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<SalesSendPreview | null>(null);

  const canSend = canManage && cfg.canSend && Boolean(cfg.api.send);
  const convert = canManage ? cfg.convert : undefined;

  async function copyNumber() {
    close();
    try {
      await navigator.clipboard.writeText(doc.number);
      toast(t("common.copied"));
    } catch {
      toast(t("common.copyFailed"));
    }
  }

  // Every kind has a PDF route, and this is the one write-free action worth
  // having on a row: the usual reason to open a document from this list is to
  // get the paper out of it.
  async function downloadPdf() {
    close();
    if (busy) return;
    setBusy(true);
    try {
      await downloadDocPdf(cfg.kind, doc.id, `${doc.number}.pdf`);
    } catch {
      toast(t("common.error"));
    } finally {
      setBusy(false);
    }
  }

  // The row's send shows the letter first, exactly as the document page does:
  // From, To, the words and the PDF, read before anything leaves. Nothing is
  // sent until `confirmSend`.
  async function send() {
    close();
    const type = SHARE_TYPE[cfg.kind];
    if (!cfg.api.send || !type || busy) return;
    setBusy(true);
    try {
      setPreview(await getSendPreview(type, doc.id));
    } catch {
      toast(t("common.error"));
    } finally {
      setBusy(false);
    }
  }

  async function confirmSend() {
    const run = cfg.api.send;
    if (!run || busy) return;
    setBusy(true);
    try {
      const next = await run(doc.id);
      setPreview(null);
      onChanged(next);
      // The send endpoint reports what happened to the email alongside the
      // document, and the four outcomes are not interchangeable: "marked as
      // sent" with no mail server behind it is a very different fact from a
      // delivered message, and the operator has to be told which one it was.
      const meta = (next as { email?: EmailMeta }).email;
      const key =
        meta?.reason === "no_recipient"
          ? "noEmail"
          : meta?.status === "sent"
            ? "sent"
            : meta?.status === "dev"
              ? "dev"
              : meta?.status === "failed"
                ? "failed"
                : null;
      toast(key ? t(`sales.sendResult.${key}`) : t("common.saved"));
    } catch {
      toast(t("common.error"));
    } finally {
      setBusy(false);
    }
  }

  async function runConvert() {
    close();
    const conv = cfg.convert;
    if (!conv || busy) return;
    setBusy(true);
    try {
      const res = await conv.run(doc.id);
      const target = res.estimate ?? res.invoice;
      if (target) navigate(`/sales/${conv.toKind}/${target.id}`);
    } catch (e) {
      // A document that was converted once answers with the id of what it
      // became. Opening that is what the operator asked for anyway, so it beats
      // an error message about a document that already exists.
      const err = e as { body?: { estimateId?: string; invoiceId?: string } };
      const existing = err.body?.estimateId ?? err.body?.invoiceId;
      if (existing) navigate(`/sales/${conv.toKind}/${existing}`);
      else toast(t("common.error"));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (busy) return;
    setBusy(true);
    try {
      await cfg.api.del(doc.id);
      setConfirmDel(false);
      onDeleted();
    } catch {
      toast(t("common.error"));
    } finally {
      setBusy(false);
    }
  }

  return (
    // Above the row-covering link overlay, or the menu would never get the click.
    <span className="relative z-[1] justify-self-end">
      <Pop
        trigger={(open) => (
          <button
            type="button"
            className={`crm-cb${open ? " is-on" : ""}`}
            title={t("common.more")}
            aria-label={t("common.more")}
            aria-haspopup="menu"
            aria-expanded={open}
          >
            <MoreHorizontal className="size-4" />
          </button>
        )}
      >
        <button
          type="button"
          className="crm-mi"
          onClick={() => {
            close();
            navigate(`/sales/${cfg.kind}/${doc.id}`);
          }}
        >
          <ArrowUpRight className="size-4" />
          {t("sales.open")}
        </button>
        <button type="button" className="crm-mi" onClick={() => void downloadPdf()}>
          <Download className="size-4" />
          {t("sales.downloadPdf")}
        </button>
        <button type="button" className="crm-mi" onClick={() => void copyNumber()}>
          <Copy className="size-4" />
          {t("sales.copyNumber")}
        </button>

        {canManage ? (
          <>
            <div className="crm-menu-sep" />
            <button
              type="button"
              className="crm-mi"
              onClick={() => {
                close();
                navigate(`/sales/${cfg.kind}/${doc.id}/edit`);
              }}
            >
              <Pencil className="size-4" />
              {t("sales.edit")}
            </button>
            {canSend && cfg.mailCompose && canMail ? (
              // The same two ways to send as the document page's Send menu:
              // a cover letter written in the mailbox, or the standard text
              // after its preview.
              <>
                <button
                  type="button"
                  className="crm-mi"
                  onClick={() => {
                    close();
                    navigate(`/email?estimate=${doc.id}`);
                  }}
                >
                  <Mail className="size-4" />
                  {t("sales.sendWithLetter")}
                </button>
                <button type="button" className="crm-mi" onClick={() => void send()}>
                  <Send className="size-4" />
                  {t("sales.sendStandard")}
                </button>
              </>
            ) : canSend ? (
              <button type="button" className="crm-mi" onClick={() => void send()}>
                <Send className="size-4" />
                {t("sales.send")}
              </button>
            ) : null}
            {convert ? (
              <button type="button" className="crm-mi" onClick={() => void runConvert()}>
                <ArrowRightLeft className="size-4" />
                {t(convert.labelKey)}
              </button>
            ) : null}

            <div className="crm-menu-sep" />
            <button
              type="button"
              className="crm-mi crm-mi-danger"
              onClick={() => {
                close();
                setConfirmDel(true);
              }}
            >
              <Trash2 className="size-4" />
              {t("sales.delete")}
            </button>
          </>
        ) : null}
      </Pop>

      {preview ? (
        <SendConfirmModal
          preview={sendPreviewFor(preview, t)}
          busy={busy}
          onCancel={() => setPreview(null)}
          onConfirm={() => void confirmSend()}
        />
      ) : null}

      {confirmDel ? (
        <Modal
          title={t("sales.deleteConfirm")}
          label={t("sales.deleteConfirm")}
          onClose={() => setConfirmDel(false)}
          footer={
            <>
              <Btn onClick={() => setConfirmDel(false)} disabled={busy}>
                {t("sales.cancel")}
              </Btn>
              <Btn tone="danger" onClick={() => void remove()} disabled={busy}>
                {t("sales.delete")}
              </Btn>
            </>
          }
        >
          <p className="crm-sub">{t("sales.deleteBody", { number: doc.number })}</p>
        </Modal>
      ) : null}
    </span>
  );
}
